/**
 * @grundfast/dawa-autocomplete — the `dawaAutocomplete(input, options)` address field, running on
 * Grundfast's DAWA-compatible `/autocomplete` after DAWA itself closed on 1 October 2026.
 *
 * Same function, same options, same `select` payload and the same CSS classes as the widget it
 * replaces; the one new thing is `apiKey`, a public (`gf_pub_`) Grundfast key.
 */
import { AutocompleteController } from './controller.ts';
import { debounce, viewTimers } from './timers.ts';
import type {
	DawaAutocomplete,
	DawaAutocompleteOptions,
	DawaSuggestion,
	FetchImpl,
	SuggestionType,
} from './types.ts';
import { autocompleteUi } from './ui.ts';
import { warn } from './warn.ts';

export type {
	DawaAutocomplete,
	DawaAutocompleteOptions,
	DawaSuggestion,
	DawaSuggestionData,
	FetchImpl,
	RenderData,
	RenderFunction,
	SuggestionType,
} from './types.ts';

export const DEFAULT_BASE_URL = 'https://api.grundfast.dk/dawa';

/** Every option this package reads. Anything else gets one console warning and is ignored. */
const KNOWN_OPTIONS = new Set<string>([
	'select',
	'apiKey',
	'baseUrl',
	'adgangsadresserOnly',
	'type',
	'fuzzy',
	'params',
	'stormodtagerpostnumre',
	'supplerendebynavn',
	'minLength',
	'retryDelay',
	'debounce',
	'multiline',
	'id',
	'render',
	'fetchImpl',
]);

const TYPES: readonly SuggestionType[] = ['vejnavn', 'adgangsadresse', 'adresse'];

/** Hosts of the closed DAWA API. A `baseUrl` left pointing at one of them is replaced. */
const CLOSED_HOSTS = /(^|\.)(dataforsyningen\.dk|aws\.dk)$/i;

function resolveBaseUrl(baseUrl: string | undefined): string {
	if (!baseUrl) return DEFAULT_BASE_URL;
	let host: string;
	try {
		host = new URL(baseUrl, 'https://x.invalid').hostname;
	} catch {
		host = '';
	}
	if (CLOSED_HOSTS.test(host)) {
		warn(
			`baseUrl ${baseUrl} is the closed DAWA API — using ${DEFAULT_BASE_URL} instead. Remove the baseUrl option to silence this.`,
		);
		return DEFAULT_BASE_URL;
	}
	return baseUrl.replace(/\/+$/, '');
}

function defaultFetch(input: HTMLInputElement): FetchImpl {
	const view = input.ownerDocument.defaultView;
	const f = view?.fetch ?? globalThis.fetch;
	return (url) => f.call(view ?? globalThis, url, { mode: 'cors', credentials: 'omit' });
}

/**
 * Turn `inputElm` into a Danish address field.
 *
 * ```js
 * import { dawaAutocomplete } from '@grundfast/dawa-autocomplete';
 * dawaAutocomplete(document.getElementById('adresse'), {
 *   apiKey: 'gf_pub_live_…',
 *   select: (valgt) => console.log(valgt.tekst, valgt.data.id),
 * });
 * ```
 */
export function dawaAutocomplete(
	inputElm: HTMLInputElement,
	options: DawaAutocompleteOptions = {},
): DawaAutocomplete {
	for (const name of Object.keys(options)) {
		if (!KNOWN_OPTIONS.has(name)) warn(`option "${name}" is not supported and is ignored`);
	}

	const apiKey = options.apiKey ?? inputElm.getAttribute('data-grundfast-key') ?? undefined;
	const baseUrl = resolveBaseUrl(options.baseUrl);
	if (!apiKey && baseUrl === DEFAULT_BASE_URL) {
		warn(
			'no apiKey — pass a public key (gf_pub_live_… or gf_pub_test_…) as options.apiKey or a data-grundfast-key attribute. Create one at https://grundfast.dk/dashboard/keys',
		);
	} else if (apiKey && /^gf_(live|test)_/.test(apiKey)) {
		warn(
			'apiKey is a SECRET key (gf_live_/gf_test_) — it is visible to everyone who opens the page and the API refuses it from a browser. Use a public key (gf_pub_…) instead.',
		);
	}

	let type: SuggestionType = options.adgangsadresserOnly ? 'adgangsadresse' : 'adresse';
	if (options.type !== undefined) {
		if (TYPES.includes(options.type)) type = options.type;
		else
			warn(`type "${String(options.type)}" is not one of ${TYPES.join(', ')}; using ${type}`);
	}

	const select = typeof options.select === 'function' ? options.select : () => undefined;

	const timers = viewTimers(inputElm);
	const controller = new AutocompleteController(
		{
			baseUrl,
			apiKey,
			type,
			fuzzy: options.fuzzy ?? true,
			params: options.params ?? {},
			stormodtagerpostnumre: options.stormodtagerpostnumre ?? true,
			supplerendebynavn: options.supplerendebynavn ?? true,
			minLength: options.minLength ?? 2,
			retryDelay: options.retryDelay ?? 500,
			fetchImpl: options.fetchImpl ?? defaultFetch(inputElm),
			timers,
		},
		{
			render: (suggestions) => ui.setSuggestions(suggestions),
			select: (item: DawaSuggestion) => {
				ui.selectAndClose(item.tekst);
				select(item);
			},
			initial: (item) => ui.selectAndClose(item.tekst),
		},
	);

	const update = (text: string, caretpos: number): void => controller.update(text, caretpos);
	const debounced =
		options.debounce && options.debounce > 0
			? debounce(update, options.debounce, 500, timers)
			: null;
	const onTextChange = debounced ?? update;

	const ui = autocompleteUi(inputElm, {
		onSelect: (item) => controller.pick(item),
		onTextChange,
		render: options.render,
		multiline: options.multiline ?? false,
	});

	if (options.id) controller.selectInitial(options.id);

	return {
		id: (uuid) => controller.selectInitial(uuid),
		destroy: () => {
			debounced?.cancel();
			controller.destroy();
			ui.destroy();
		},
		selected: () => controller.selected,
	};
}
